<template>
  <div class='GraficoLinha'>
    <div v-if='legenda' class='legenda'>
      <span v-for='(serie, i) in series' :key='i' class='itemLegenda'>
        <span class='traco' :style='{ background: serie.cor }'></span>
        {{ serie.nome }}
      </span>
    </div>
    <svg :viewBox='`0 0 ${largura} ${altura}`' class='svg'>
      <g v-for='(tick, i) in ticks' :key='"t" + i'>
        <line
          :x1='margem.esquerda' :x2='largura - margem.direita'
          :y1='y(tick)' :y2='y(tick)'
          class='grade'
        />
        <text :x='margem.esquerda - 10' :y='y(tick)' class='textoEixoY'>{{ formatar(tick) }}</text>
      </g>
      <path
        v-for='(faixa, f) in faixas' :key='"f" + f'
        :d='area(faixa)' :fill='faixa.cor'
      />
      <template v-for='(label, i) in labels' :key='"l" + i'>
        <text
          v-if='i % passoLabel === 0'
          :x='x(i)' :y='altura - 6'
          class='textoEixoX'
        >{{ label }}</text>
      </template>
      <g v-for='(serie, s) in seriesInvertidas' :key='"s" + s'>
        <path :d='caminho(serie.valores)' :stroke='serie.cor' :stroke-width='serie.pontos ? 2.5 : 1.5' class='linha' />
        <template v-if='serie.pontos'>
          <template v-for='(valor, i) in serie.valores' :key='i'>
            <circle
              v-if='valor !== null'
              :cx='x(i)' :cy='y(valor)' r='4'
              :stroke='serie.cor'
              class='ponto'
            />
          </template>
        </template>
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'GraficoLinha',
  props: {
    labels: { type: Array, required: true },
    // [{ nome, cor, valores: [Number], pontos: Boolean }] (valor null não é desenhado)
    series: { type: Array, required: true },
    // áreas sombreadas: [{ cor, min: [Number], max: [Number] }]
    faixas: { type: Array, default: () => [] },
    legenda: { type: Boolean, default: true },
    divisoes: { type: Number, default: 4 }
  },
  data () {
    return {
      largura: 600,
      altura: 220,
      margem: { topo: 10, direita: 12, base: 30, esquerda: 40 }
    }
  },
  computed: {
    maximo () {
      // 0 garante um número mesmo com as séries vazias (antes dos dados chegarem)
      const max = Math.max(
        0,
        ...this.series.flatMap(s => s.valores),
        ...this.faixas.flatMap(f => f.max)
      )
      return Math.ceil(max) || 1
    },
    // no máximo 8 rótulos no eixo X para não sobrepor
    passoLabel () {
      return Math.ceil(this.labels.length / 8) || 1
    },
    ticks () {
      return Array.from({ length: this.divisoes + 1 }, (_, i) => this.maximo / this.divisoes * i)
    },
    // a primeira série é desenhada por último, ficando por cima
    seriesInvertidas () {
      return [...this.series].reverse()
    }
  },
  methods: {
    x (i) {
      const util = this.largura - this.margem.esquerda - this.margem.direita
      return this.margem.esquerda + util / Math.max(this.labels.length - 1, 1) * i
    },
    y (valor) {
      const util = this.altura - this.margem.topo - this.margem.base
      return this.margem.topo + util * (1 - valor / this.maximo)
    },
    formatar (numero) {
      return numero.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
    },
    // curva monotônica: suave sem ultrapassar os valores dos pontos
    caminho (valores) {
      const p = valores
        .map((v, i) => v === null ? null : [this.x(i), this.y(v)])
        .filter(pt => pt !== null)
      if (p.length < 2) return ''
      return `M${p[0][0]},${p[0][1]}` + this.curva(p)
    },
    // contorno de cima da esquerda para a direita, volta pelo de baixo
    area (faixa) {
      const cima = faixa.max.map((v, i) => [this.x(i), this.y(v)])
      const baixo = faixa.min.map((v, i) => [this.x(i), this.y(v)]).reverse()
      if (cima.length < 2) return ''
      return `M${cima[0][0]},${cima[0][1]}` + this.curva(cima) +
        ` L${baixo[0][0]},${baixo[0][1]}` + this.curva(baixo) + ' Z'
    },
    curva (p) {
      const inclinacoes = p.slice(1).map((pt, i) => (pt[1] - p[i][1]) / (pt[0] - p[i][0]))
      const tangentes = p.map((_, i) => {
        if (i === 0) return inclinacoes[0]
        if (i === p.length - 1) return inclinacoes[i - 1]
        const a = inclinacoes[i - 1]
        const b = inclinacoes[i]
        return a * b <= 0 ? 0 : (a + b) / 2
      })
      let d = ''
      for (let i = 0; i < p.length - 1; i++) {
        const dx = (p[i + 1][0] - p[i][0]) / 3
        d += ` C${p[i][0] + dx},${p[i][1] + tangentes[i] * dx}` +
          ` ${p[i + 1][0] - dx},${p[i + 1][1] - tangentes[i + 1] * dx}` +
          ` ${p[i + 1][0]},${p[i + 1][1]}`
      }
      return d
    }
  }
}
</script>

<style scoped>
.legenda {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 11px;
  color: #616161;
}
.itemLegenda {
  display: flex;
  align-items: center;
  gap: 6px;
}
.traco {
  width: 14px;
  height: 3px;
  border-radius: 2px;
}
.svg {
  display: block;
  width: 100%;
  height: auto;
}
.grade {
  stroke: #ebebeb;
  stroke-width: 1;
}
.textoEixoY {
  font-size: 10px;
  fill: #616161;
  text-anchor: end;
  dominant-baseline: middle;
}
.textoEixoX {
  font-size: 10px;
  fill: #616161;
  text-anchor: middle;
}
.linha {
  fill: none;
}
.ponto {
  fill: #fff;
  stroke-width: 2;
}
</style>
